import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useState, useCallback } from "react";

/**
 * Custom hook for calculating distance between coordinates using Google Maps Routes API
 */
export const useDistanceMatrix = () => {
  const routesLib = useMapsLibrary("routes");
  const [state, setState] = useState({
    isCalculating: false,
    error: null,
  });

  const calculateDistance = useCallback(
    async (coordinates, options = {}) => {
      setState((prev) => ({ ...prev, isCalculating: true, error: null }));

      // Initialize result object
      const result = {
        totalDistance: null,
        error: null,
        response: null,
      };

      try {
        if (!routesLib || !coordinates || !coordinates.length) {
          const errorMsg = "Missing routes library or coordinates are empty";
          setState((prev) => ({
            ...prev,
            error: errorMsg,
            isCalculating: false,
          }));
          result.error = errorMsg;
          return result;
        }

        const defaultOptions = {
          avoidFerries: true,
          travelMode: "DRIVING",
        };

        const mergedOptions = { ...defaultOptions, ...options };

        const origins = [];
        const destinations = [];
        let len = coordinates.length;

        coordinates.forEach((coord, index) => {
          const point = { lat: coord.latitude, lng: coord.longitude };
          if (index % 2 === 0) {
            origins.push(point);
          } else {
            destinations.push(point);
          }
        });

        if (origins.length === 0 || destinations.length === 0) {
          const errorMsg = "Not enough coordinates to calculate distance";
          setState((prev) => ({
            ...prev,
            error: errorMsg,
            isCalculating: false,
          }));
          result.error = errorMsg;
          return result;
        }

        const distanceMatrixService = new routesLib.DistanceMatrixService();
        const travelMode =
          routesLib.TravelMode[mergedOptions.travelMode] ||
          routesLib.TravelMode.DRIVING;

        const request = {
          origins,
          destinations,
          travelMode,
          avoidFerries: mergedOptions.avoidFerries,
        };

        const res = await distanceMatrixService.getDistanceMatrix(request);
        result.response = res;

        if (!res || !res.rows) {
          throw new Error("Invalid response structure: missing rows");
        }

        let og = 0; // origin index
        let dt = 0; // destination index
        let totalDistanceValue = 0;

        while (len > 1) {
          // Validate array indexes to prevent out-of-bounds errors
          if (og >= res.rows.length) {
            throw new Error(
              `Origin index ${og} out of bounds (max: ${res.rows.length - 1})`
            );
          }

          const row = res.rows[og];
          if (!row || !row.elements) {
            throw new Error(`Missing elements in row ${og}`);
          }

          if (dt >= row.elements.length) {
            throw new Error(
              `Destination index ${dt} out of bounds (max: ${
                row.elements.length - 1
              })`
            );
          }

          const element = row.elements[dt];
          if (!element || !element.distance || element.status !== "OK") {
            throw new Error(
              `Invalid element at [${og}][${dt}]: ${JSON.stringify(element)}`
            );
          }

          const distance = element.distance.value;
          totalDistanceValue = totalDistanceValue + distance;

          if (og === dt) {
            og = og + 1;
          } else {
            dt = dt + 1;
          }
          len--;
        }

        result.totalDistance = (totalDistanceValue / 1000).toFixed(2);
        return result;
      } catch (err) {
        setState((prev) => ({
          ...prev,
          error: err.message,
          isCalculating: false,
        }));
        result.error = err.message;
        return result;
      } finally {
        setState((prev) => ({ ...prev, isCalculating: false }));
      }
    },
    [routesLib]
  );

  return {
    calculateDistance,
    isCalculating: state.isCalculating,
    error: state.error,
  };
};
